import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import { useTranslation } from 'react-i18next';
import {
  AlreadyPrrocessIcon,
  Block,
  Button,
  CanceledIcon,
  ClockIcon,
  Column,
  DateCellComponent,
  FlexContainer,
  NotFoundIcon,
  ScannerInput,
  SoundNotificationContext,
  Span,
  TableSimple,
  TriangleAlertIcon,
  useNotifications,
} from '@uni-space/uni-components';
import { ParcelDto } from '@shared/types/parcel';
import { getErrorMessageAsync } from '@shared/api';
import { cancelParcel, findParcelsByTrackNumbers, uploadCancellationPhoto } from '../api';
import { CancellationReasonItem } from '../model/useCancellationReasons';
import {
  CancellationPhotoRow,
  CancellationPhotoRowStatus,
  CancellationPhotoStage,
  LastShot,
  dataUrlToBlob,
} from '../model/cancellationPhoto';
import CancellationReasonModal from './CancellationReasonModal';
import './CancellationPhotoCapture.scss';

/**
 * Фото-фиксация отмены: запрещённое или опасное вложение. Шаги оператора по одной посылке:
 * скан (сервер отвечает, отменена она или нет) → для не отменённой модалка с причиной, отмена уходит
 * сразу тем же запросом, что и обычная отмена → снимок вложения уходит в Media (тип «отмена»),
 * и посылка закрывается в журнале. Уже отменённая посылка проходит без модалки — только снимок.
 * Пока снимок не сделан, сканер заблокирован: иначе отменённая посылка осталась бы без фиксации.
 * Снимок берётся с камеры так же, как на ручной приёмке (react-webcam, PNG без потерь).
 */
export default function CancellationPhotoCapture() {
  const { t, i18n } = useTranslation();
  const { addNotification } = useNotifications();
  const { onSoundSuccess, onSoundError, onSoundNotFound, onSoundAlready } = useContext(SoundNotificationContext);

  const webcamRef = useRef<Webcam | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const [stage, setStage] = useState<CancellationPhotoStage>({ kind: 'idle' });
  const [isSearching, setIsSearching] = useState(false);
  const [isSending, setIsSending] = useState(false);
  /** Причина, по которой сейчас идёт отмена; null — модалка ждёт выбора. */
  const [cancelingReason, setCancelingReason] = useState<CancellationReasonItem | null>(null);
  const [cancelError, setCancelError] = useState<string | undefined>(undefined);
  const [lastShot, setLastShot] = useState<LastShot | null>(null);
  const [rows, setRows] = useState<CancellationPhotoRow[]>([]);

  const isBusy = isSearching || isSending || cancelingReason !== null;

  const counts = useMemo(() => {
    const acc = { canceled: 0, alreadyCanceled: 0, awaitingPhoto: 0, notFound: 0, error: 0 };
    for (const row of rows) {
      switch (row.status) {
        case CancellationPhotoRowStatus.Canceled:
          acc.canceled++;
          break;
        case CancellationPhotoRowStatus.AlreadyCanceled:
          acc.alreadyCanceled++;
          break;
        case CancellationPhotoRowStatus.AwaitingPhoto:
          acc.awaitingPhoto++;
          break;
        case CancellationPhotoRowStatus.NotFound:
          acc.notFound++;
          break;
        case CancellationPhotoRowStatus.Error:
          acc.error++;
          break;
      }
    }
    return acc;
  }, [rows]);

  /** Возвращает id строки: отмена и снимок — разные шаги, и второй дописывает результат в ту же строку. */
  const addRow = useCallback((row: Omit<CancellationPhotoRow, 'id' | 'at'>) => {
    const at = new Date();
    const id = `${row.trackNumber}-${at.getTime()}`;
    setRows((prev) => [{ ...row, id, at }, ...prev]);
    return id;
  }, []);

  const updateRow = useCallback((id: string, patch: Partial<Omit<CancellationPhotoRow, 'id' | 'at'>>) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }, []);

  const onScanSubmit = async (rawTrackNumber: string) => {
    const trackNumber = rawTrackNumber.trim();
    if (!trackNumber || isBusy || stage.kind !== 'idle') return;

    setIsSearching(true);
    try {
      // При одном треке бэкенд ищет по StartsWith — точное совпадение отбираем сами.
      const found = (await findParcelsByTrackNumbers([trackNumber])).find((item) => item.trackNumber === trackNumber);

      if (!found) {
        onSoundNotFound();
        addRow({ trackNumber, status: CancellationPhotoRowStatus.NotFound });
        addNotification({ type: 'error', message: t('cancellationPhoto.notifications.notFound', { trackNumber }) });
        return;
      }

      if (found.isCanceled) {
        // Отменять нечего, причину не спрашиваем — остаётся снимок.
        onSoundAlready();
        setStage({ kind: 'photo', parcel: found, reason: null, rowId: null });
        addNotification({
          type: 'warning',
          message: t('cancellationPhoto.notifications.alreadyCanceledScan', { trackNumber }),
        });
        return;
      }

      setCancelError(undefined);
      setStage({ kind: 'reason', parcel: found });
    } catch (err) {
      onSoundError();
      addNotification({ type: 'error', message: await getErrorMessageAsync(err, t('bulkCancel.searchError')) });
    } finally {
      setIsSearching(false);
    }
  };

  const onSelectReason = async (reason: CancellationReasonItem) => {
    if (stage.kind !== 'reason' || cancelingReason !== null) return;

    const { parcel } = stage;
    setCancelingReason(reason);
    setCancelError(undefined);
    try {
      await cancelParcel(parcel.trackNumber, reason.reasonId, reason.label);

      const rowId = addRow({
        trackNumber: parcel.trackNumber,
        status: CancellationPhotoRowStatus.AwaitingPhoto,
        reasonLabel: reason.label,
      });
      setStage({ kind: 'photo', parcel: { ...parcel, isCanceled: true }, reason, rowId });
      onSoundSuccess();
      addNotification({
        type: 'success',
        message: t('cancellationPhoto.notifications.canceled', { trackNumber: parcel.trackNumber }),
      });
    } catch (err) {
      // Посылка не отменена — модалка остаётся открытой, оператор повторит или выберет другую причину.
      const message = await getErrorMessageAsync(err, t('cancellationPhoto.notifications.cancelError'));
      onSoundError();
      setCancelError(message);
      addNotification({ type: 'error', message });
    } finally {
      setCancelingReason(null);
    }
  };

  const onCloseReasonModal = () => {
    if (cancelingReason !== null) return;
    setCancelError(undefined);
    setStage({ kind: 'idle' });
  };

  const onCapture = async () => {
    if (stage.kind !== 'photo' || isBusy) return;

    if (!cameraReady) {
      onSoundError();
      addNotification({ type: 'error', message: t('cancellationPhoto.notifications.cameraNotReady') });
      return;
    }

    const shot = webcamRef.current?.getScreenshot() ?? null;
    if (!shot) {
      onSoundError();
      addNotification({ type: 'error', message: t('cancellationPhoto.notifications.captureFailed') });
      return;
    }

    const { parcel, reason, rowId } = stage;
    const { trackNumber } = parcel;
    setIsSending(true);
    try {
      const blob = await dataUrlToBlob(shot);
      const result = await uploadCancellationPhoto(trackNumber, blob);

      setLastShot({ trackNumber, dataUrl: shot });

      const done = {
        status: reason ? CancellationPhotoRowStatus.Canceled : CancellationPhotoRowStatus.AlreadyCanceled,
        reasonLabel: reason?.label,
        isPhotoNew: result.isPhotoNew,
        errorMessage: result.isPhotoNew ? undefined : t('cancellationPhoto.photoAlreadyExisted'),
      };

      if (rowId) updateRow(rowId, done);
      else addRow({ trackNumber, ...done });

      setStage({ kind: 'idle' });
      onSoundSuccess();
      addNotification({
        type: 'success',
        message: t('cancellationPhoto.notifications.photoSaved', { trackNumber }),
      });
    } catch (err) {
      // Отмена (если была) уже прошла, поэтому посылка остаётся выбранной: оператор поправит кадр и повторит.
      const message = await getErrorMessageAsync(err, t('cancellationPhoto.notifications.photoError'));
      onSoundError();
      if (rowId) updateRow(rowId, { errorMessage: message });
      else addRow({ trackNumber, status: CancellationPhotoRowStatus.Error, errorMessage: message });
      addNotification({ type: 'error', message });
    } finally {
      setIsSending(false);
    }
  };

  /** Отказ от снимка: посылку отпускаем, но в журнале она остаётся незакрытой — это видно по статусу. */
  const onSkipPhoto = () => {
    if (stage.kind !== 'photo' || isSending) return;

    if (stage.rowId) {
      updateRow(stage.rowId, { errorMessage: t('cancellationPhoto.photoSkipped') });
    }
    setStage({ kind: 'idle' });
  };

  const renderStatus = useCallback(
    (row: CancellationPhotoRow) => {
      switch (row.status) {
        case CancellationPhotoRowStatus.Canceled:
          return { icon: <CanceledIcon color="green" size="s" />, label: t('cancellationPhoto.status.canceled') };
        case CancellationPhotoRowStatus.AlreadyCanceled:
          return {
            icon: <AlreadyPrrocessIcon color="#f57c00" size="s" />,
            label: t('cancellationPhoto.status.alreadyCanceled'),
          };
        case CancellationPhotoRowStatus.AwaitingPhoto:
          return {
            icon: <ClockIcon color="#f57c00" size="s" />,
            label: t('cancellationPhoto.status.awaitingPhoto'),
          };
        case CancellationPhotoRowStatus.NotFound:
          return { icon: <NotFoundIcon color="#EF6D7C" size="s" />, label: t('cancellationPhoto.status.notFound') };
        case CancellationPhotoRowStatus.Error:
          return { icon: <TriangleAlertIcon color="#EF6D7C" size="s" />, label: t('cancellationPhoto.status.error') };
      }
    },
    [t],
  );

  const columns: Column<CancellationPhotoRow>[] = useMemo(
    () => [
      {
        header: t('cancellationPhoto.table.time'),
        accessor: 'at',
        textAlign: 'center',
        flex: 0.6,
        minWidth: '90px',
        renderCell: (row) => <DateCellComponent justify="center" date={row.at} type="onlyTime" zone="local" />,
      },
      {
        header: t('tableHeader.trackNumber'),
        accessor: 'trackNumber',
        textAlign: 'center',
        flex: 1,
        minWidth: '180px',
        renderCell: (row) => (
          <span
            className={
              row.status === CancellationPhotoRowStatus.NotFound || row.status === CancellationPhotoRowStatus.Error
                ? 'cpc-code cpc-code--danger'
                : 'cpc-code'
            }
          >
            {row.trackNumber}
          </span>
        ),
      },
      {
        header: t('tableHeader.status'),
        accessor: 'status',
        textAlign: 'center',
        flex: 1.2,
        minWidth: '200px',
        renderCell: (row) => {
          const { icon, label } = renderStatus(row);
          return (
            <div className="cpc-status">
              {icon}
              <div>{label}</div>
            </div>
          );
        },
      },
      {
        header: t('cancellationPhoto.table.reason'),
        accessor: 'reasonLabel',
        textAlign: 'center',
        flex: 1,
        minWidth: '140px',
        renderCell: (row) => row.reasonLabel ?? '—',
      },
      {
        header: t('cancellationPhoto.table.comment'),
        accessor: 'errorMessage',
        textAlign: 'center',
        flex: 1.5,
        minWidth: '160px',
        renderCell: (row) =>
          row.errorMessage ? (
            <span className={row.status === CancellationPhotoRowStatus.Error ? 'cpc-errorMessage' : 'cpc-note'}>
              {row.errorMessage}
            </span>
          ) : (
            '—'
          ),
      },
    ],
    [renderStatus, t],
  );

  const parcel = stage.kind === 'idle' ? null : stage.parcel;
  const parcelWeight = parcel ? (parcel.actualWeight?.value ?? parcel.weight?.value ?? null) : null;
  const isAwaitingPhoto = stage.kind === 'photo';

  return (
    <div className="cpc-page">
      {stage.kind === 'reason' && (
        <CancellationReasonModal
          parcel={stage.parcel}
          pendingReason={cancelingReason}
          errorMessage={cancelError}
          onSelect={onSelectReason}
          onClose={onCloseReasonModal}
        />
      )}

      <div className="cpc-card">
        <div className="cpc-cardHead">
          <div>
            <div className="cpc-title">{t('cancellationPhoto.title')}</div>
            <div className="cpc-subtitle">{t('cancellationPhoto.subtitle')}</div>
          </div>
        </div>

        <div className="cpc-topGrid">
          <div className="cpc-left">
            <div className="cpc-field">
              <ScannerInput
                label={t('cancellationPhoto.scan.label')}
                buttonLabel={t('cancellationPhoto.scan.button')}
                isLoading={isSearching}
                disabled={isBusy || stage.kind !== 'idle'}
                maxValueLength={64}
                lang={i18n.language}
                isFocusFilter={stage.kind === 'reason'}
                onSubmit={onScanSubmit}
              />
              <div className={isAwaitingPhoto ? 'cpc-hint cpc-hint--error' : 'cpc-hint'}>
                {isAwaitingPhoto ? t('cancellationPhoto.scan.lockedHint') : t('cancellationPhoto.scan.hint')}
              </div>
            </div>

            {parcel ? (
              <div className={isAwaitingPhoto ? 'cpc-parcel cpc-parcel--canceled' : 'cpc-parcel'}>
                <div className="cpc-parcelHead">
                  <span className="cpc-code">{parcel.trackNumber}</span>
                  <ParcelStateChips parcel={parcel} reasonLabel={stage.kind === 'photo' ? stage.reason?.label : null} />
                </div>
                <div className="cpc-parcelGrid">
                  <div>
                    <div className="cpc-muted">{t('cancellationPhoto.parcel.service')}</div>
                    <div>{parcel.service?.name ?? '—'}</div>
                  </div>
                  <div>
                    <div className="cpc-muted">{t('cancellationPhoto.parcel.weight')}</div>
                    <div>{parcelWeight !== null ? `${parcelWeight} g` : '—'}</div>
                  </div>
                  <div>
                    <div className="cpc-muted">{t('tableHeader.regionZone')}</div>
                    <div>{parcel.regionZone ?? '—'}</div>
                  </div>
                  <div>
                    <div className="cpc-muted">{t('bagCode')}</div>
                    <div>{parcel.bagCode ?? '—'}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="cpc-parcel cpc-parcel--empty">{t('cancellationPhoto.parcel.empty')}</div>
            )}

            <FlexContainer gap="8px">
              <Button
                type="error"
                width="100%"
                onClick={onCapture}
                disabled={!isAwaitingPhoto || isBusy}
                loading={isSending}
              >
                {t('cancellationPhoto.capture')}
              </Button>
              <Button width="160px" disabled={!isAwaitingPhoto || isSending} onClick={onSkipPhoto}>
                {t('cancellationPhoto.skipPhoto')}
              </Button>
            </FlexContainer>
            <div className="cpc-hint">{t('cancellationPhoto.hint')}</div>
          </div>

          <div className="cpc-cameraCard">
            <div className="cpc-cardHead">
              <div>
                <div className="cpc-title">{t('cancellationPhoto.camera.title')}</div>
                <div className="cpc-muted">
                  <Span>{t('cancellationPhoto.camera.statusLabel')} </Span>
                  {cameraError ? (
                    <Span fw={600} color="red">
                      {t('cancellationPhoto.camera.errorWithReason', { reason: cameraError })}
                    </Span>
                  ) : cameraReady ? (
                    <Span>{t('cancellationPhoto.camera.ready')}</Span>
                  ) : (
                    <Span>{t('cancellationPhoto.camera.requesting')}</Span>
                  )}
                </div>
              </div>
            </div>

            <div className="cpc-webcamWrap">
              {/* Как на ручной приёмке: PNG без потерь в исходном разрешении камеры, а не в размере элемента */}
              <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/png"
                forceScreenshotSourceSize
                videoConstraints={{
                  facingMode: 'environment',
                  width: { ideal: 1280 },
                  height: { ideal: 720 },
                }}
                className="cpc-webcam"
                onUserMedia={() => {
                  setCameraReady(true);
                  setCameraError(null);
                }}
                onUserMediaError={(e) => {
                  setCameraReady(false);
                  const reason = (e as { name?: string })?.name ?? t('cancellationPhoto.camera.permissionDenied');
                  setCameraError(reason);
                }}
              />
            </div>

            {lastShot && (
              <div className="cpc-lastShot">
                <img className="cpc-lastShotImage" src={lastShot.dataUrl} alt={lastShot.trackNumber} />
                <div>
                  <div className="cpc-muted">{t('cancellationPhoto.camera.lastShot')}</div>
                  <div className="cpc-code">{lastShot.trackNumber}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="cpc-card">
        <div className="cpc-cardHead">
          <div className="cpc-title">{t('cancellationPhoto.resultsTitle')}</div>
          <div className="cpc-summary">
            <span className="cpc-chip cpc-chip--success">
              {t('cancellationPhoto.summary.canceled', { n: counts.canceled })}
            </span>
            <span className="cpc-chip cpc-chip--warning">
              {t('cancellationPhoto.summary.alreadyCanceled', { n: counts.alreadyCanceled })}
            </span>
            <span className="cpc-chip cpc-chip--warning">
              {t('cancellationPhoto.summary.awaitingPhoto', { n: counts.awaitingPhoto })}
            </span>
            <span className="cpc-chip cpc-chip--danger">
              {t('cancellationPhoto.summary.notFound', { n: counts.notFound })}
            </span>
            <span className="cpc-chip cpc-chip--danger">{t('cancellationPhoto.summary.error', { n: counts.error })}</span>
          </div>
        </div>

        <TableSimple<CancellationPhotoRow>
          columns={columns}
          data={rows}
          enableColumnResizing={false}
          withOutPagination
          isLoading={false}
          emptyPlaceholder={t('cancellationPhoto.table.empty')}
          mobileWidth={680}
          maxHeightTable="calc(100dvh - 640px)"
        />
      </div>
    </div>
  );
}

function ParcelStateChips({ parcel, reasonLabel }: { parcel: ParcelDto; reasonLabel?: string | null }) {
  const { t } = useTranslation();

  return (
    <Block sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {parcel.isCanceled ? (
        <span className="cpc-chip cpc-chip--warning">
          {reasonLabel
            ? t('cancellationPhoto.parcel.canceledWithReason', { reason: reasonLabel })
            : t('cancellationPhoto.parcel.canceled')}
        </span>
      ) : (
        <span className="cpc-chip cpc-chip--success">{t('cancellationPhoto.parcel.active')}</span>
      )}
      {parcel.isQualityControl && <span className="cpc-chip">{t('cancellationPhoto.parcel.qualityControl')}</span>}
    </Block>
  );
}
